<template>
  <b-modal
      :visible.sync="visible"
      size="xl"
      centered
      hide-footer
      :title="editingSchedule ? 'Editar horario' : 'Nuevo horario'"
      @hidden="$emit('update:visible', false)"
  >
    <div class="sidebar-content">
      <validation-observer ref="scheduleForm">
        <b-form @submit.prevent="handleSubmit">
          <b-form-group label="" label-for="schedule-name" class="mb-0">
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="Nombre del horario" label-for="schedule-name" class="mb-0">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <b-form-input
                        id="schedule-name"
                        v-model="form.name"
                        placeholder="Nombre"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Tipo de jornada" label-for="schedule-type" class="mb-1">
                  <b-form-radio-group
                      v-model="form.type"
                      id="schedule-type"
                      name="schedule-type"
                      class="d-flex align-items-center"
                  >
                    <b-form-radio value="continua" class="mr-2">Jornada continua</b-form-radio>
                    <b-form-radio value="partida">Jornada partida</b-form-radio>
                  </b-form-radio-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form-group>
          <b-form-group label="Horario semanal" label-for="schedule-week" class="mb-0 mt-2">
            <b-row>
              <b-col cols="12">
                <div v-for="day in daysOfWeek" :key="day.key" class="mb-1">
                  <b-row class="align-items-center">
                    <b-col cols="3">
                      <b-form-checkbox v-model="form.days[day.key].enabled">
                        {{ day.label }}
                      </b-form-checkbox>
                    </b-col>
                    <b-col cols="3">
                      <b-form-checkbox v-model="form.days[day.key].is_night_shift" :disabled="!form.days[day.key].enabled">
                        Nocturno
                      </b-form-checkbox>
                    </b-col>
                    <b-col cols="6" class="text-right">
                      <span v-if="form.days[day.key].enabled" class="mr-2">
                        <template v-if="form.type === 'partida'">
                          {{ calculateWorkHours(day.key) }} h / {{ calculateBreakMinutes(day.key) }} min descanso
                        </template>
                        <template v-else>
                          {{ calculateWorkHoursIntensive(day.key) }} h / {{ form.days[day.key].restMinutes || 0 }} min descanso
                        </template>
                      </span>
                      <b-dropdown size="sm" variant="outline-primary" right>
                        <template #button-content>
                          <CopyIcon size="18" class="align-middle" />
                        </template>
                        <b-dropdown-item v-for="dest in daysOfWeek.filter(d => d.key !== day.key)" :key="dest.key" @click="copyDay(day.key, dest.key)">
                          Copiar a {{ dest.label }}
                        </b-dropdown-item>
                      </b-dropdown>
                    </b-col>
                  </b-row>
                  <b-row class="align-items-center mt-1">
                    <template v-if="form.type === 'partida'">
                      <b-col cols="3" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].start_time" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Entrada" @input="updateRestMinutes(day.key)" />
                      </b-col>
                      <b-col cols="3" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].end_time" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Salida" @input="updateRestMinutes(day.key)" />
                      </b-col>
                      <b-col cols="3" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].start_time2" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Entrada" @input="updateRestMinutes(day.key)" />
                      </b-col>
                      <b-col cols="3" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].end_time2" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Salida" @input="updateRestMinutes(day.key)" />
                      </b-col>
                    </template>
                    <template v-else>
                      <b-col cols="4" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].start_time" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Entrada" />
                      </b-col>
                      <b-col cols="4" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-timepicker v-model="form.days[day.key].end_time" :disabled="!form.days[day.key].enabled" local="es" :no-close-button="true" :hide-header="true" placeholder="Salida" />
                      </b-col>
                      <b-col cols="4" class="py-0 px-1 d-flex justify-content-center">
                        <b-form-input v-model="form.days[day.key].restMinutes" :disabled="!form.days[day.key].enabled" type="number" min="0" placeholder="Min. descanso" />
                      </b-col>
                    </template>
                  </b-row>
                </div>
              </b-col>
            </b-row>
          </b-form-group>
          <div class="text-right mt-2">
            <b-button
                variant="outline-primary"
                class="mr-1"
                @click="$emit('update:visible', false)"
            >
              Cancelar
            </b-button>
            <b-button
                type="submit"
                variant="primary"
            >
              {{ editingSchedule ? 'Guardar cambios' : 'Crear horario' }}
            </b-button>

          </div>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BModal, BForm, BFormGroup, BFormInput, BFormRadio, BFormRadioGroup, BFormCheckbox, BFormTimepicker, BButton, BRow, BCol, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { CopyIcon } from 'vue-feather-icons'

// Utilidad para asegurar que days tenga todas las claves de la semana y enabled correcto
function ensureAllDays(obj) {
  const days = [
    { key: 'monday', enabled: true },
    { key: 'tuesday', enabled: true },
    { key: 'wednesday', enabled: true },
    { key: 'thursday', enabled: true },
    { key: 'friday', enabled: true },
    { key: 'saturday', enabled: false },
    { key: 'sunday', enabled: false },
  ]
  const result = {}
  days.forEach(day => {
    result[day.key] = obj && obj[day.key] ? { ...obj[day.key] } : {}
    if (typeof result[day.key].enabled === 'undefined') {
      result[day.key].enabled = day.enabled
    }
    // Eliminar 'work' si existe
    if ('work' in result[day.key]) delete result[day.key].work
  })
  return result
}

export default {
  name: 'ScheduleSidebar',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    BFormCheckbox,
    BFormTimepicker,
    BButton,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    CopyIcon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editingSchedule: {
      type: Object,
      default: null,
    },
    daysOfWeek: {
      type: Array,
      required: true,
    },
    initialForm: {
      type: Object,
      required: true,
    },
  },
  data() {
    // Aseguramos que siempre haya form.days inicializado correctamente y con todas las claves
    const initial = JSON.parse(JSON.stringify(this.initialForm))
    if (!initial.days) {
      initial.days = initial.week || {}
    }
    initial.days = ensureAllDays(initial.days)

    return {
      form: initial,
      copyFrom: '',
      copyToDay: '',
    }
  },
  watch: {
    initialForm(newVal) {
      console.log('[ScheduleSidebar] initialForm changed', newVal)
      const initial = JSON.parse(JSON.stringify(newVal))
      if (!initial.days) {
        initial.days = initial.week || {}
      }
      initial.days = ensureAllDays(initial.days)

      this.form = initial
      console.log('[ScheduleSidebar] form after initialForm change', this.form)
    },
    editingSchedule(newVal) {
      console.log('[ScheduleSidebar] editingSchedule changed', newVal)
      if (newVal) {
        const initial = JSON.parse(JSON.stringify(newVal))
        if (!initial.days) {
          initial.days = initial.week || {}
        }
        initial.days = ensureAllDays(initial.days)

        this.form = initial
        console.log('[ScheduleSidebar] form after editingSchedule change', this.form)
      }
    },
  },
  mounted() {
    console.log('[ScheduleSidebar] mounted, initialForm:', this.initialForm)
    console.log('[ScheduleSidebar] mounted, form:', this.form)
  },
  methods: {
    async handleSubmit() {
      const valid = await this.$refs.scheduleForm.validate()
      if (!valid) return
      // Limpiar work y solo enviar enabled
      const daysClean = {}
      Object.entries(this.form.days).forEach(([key, val]) => {
        const { work, ...rest } = val
        daysClean[key] = { ...rest }
      })
      const formToSend = {
        ...this.form,
        days: daysClean,
      }
      delete formToSend.week
      this.$emit('submit', formToSend)
      this.$emit('update:visible', false)
    },
    calculateWorkHours(dayKey) {
      const d = this.form.days[dayKey]
      // Jornada partida: (end_time - start_time) + (end_time2 - start_time2)
      if (d.start_time && d.end_time && d.start_time2 && d.end_time2) {
        const s1 = new Date(`1970-01-01T${d.start_time}`)
        let e1 = new Date(`1970-01-01T${d.end_time}`)
        const s2 = new Date(`1970-01-01T${d.start_time2}`)
        let e2 = new Date(`1970-01-01T${d.end_time2}`)

        // Si la hora de fin es menor que la de inicio, es un turno nocturno (cruza medianoche)
        if (e1 < s1) {
          e1 = new Date(`1970-01-02T${d.end_time}`)
        }
        if (e2 < s2) {
          e2 = new Date(`1970-01-02T${d.end_time2}`)
        }

        const workMs = (e1 - s1) + (e2 - s2)
        const totalMinutes = Math.round(workMs / 60000)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        return `${hours}:${minutes.toString().padStart(2, '0')}`
      }
      return '0:00'
    },
    calculateBreakMinutes(dayKey) {
      const d = this.form.days[dayKey]
      // Descanso = (start_time2 - end_time)
      if (d.end_time && d.start_time2) {
        const e1 = new Date(`1970-01-01T${d.end_time}`)
        let s2 = new Date(`1970-01-01T${d.start_time2}`)

        // Si start_time2 es menor que end_time, el descanso cruza la medianoche
        if (s2 < e1) {
          s2 = new Date(`1970-01-02T${d.start_time2}`)
        }

        const breakMs = s2 - e1
        const mins = Math.round(breakMs / 60000)
        return mins > 0 ? mins : 0
      }
      return 0
    },
    updateRestMinutes(dayKey) {
      // Actualizar restMinutes cuando cambian los horarios en jornada partida
      if (this.form.type === 'partida') {
        const breakMins = this.calculateBreakMinutes(dayKey)
        this.$set(this.form.days[dayKey], 'restMinutes', breakMins)
      }
    },
    calculateWorkHoursIntensive(dayKey) {
      const d = this.form.days[dayKey]
      if (d.start_time && d.end_time) {
        const s = new Date(`1970-01-01T${d.start_time}`)
        let e = new Date(`1970-01-01T${d.end_time}`)

        // Si la hora de fin es menor que la de inicio, es un turno nocturno (cruza medianoche)
        if (e < s) {
          e = new Date(`1970-01-02T${d.end_time}`)
        }

        const workMs = e - s
        const totalMinutes = Math.round(workMs / 60000)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        return `${hours}:${minutes.toString().padStart(2, '0')}`
      }
      return '0:00'
    },
    showCopyTo(dayKey) {
      this.copyFrom = dayKey
      this.copyToDay = ''
    },
    copyDayOptions(dayKey) {
      return this.daysOfWeek.filter(d => d.key !== dayKey).map(d => ({ value: d.key, text: d.label }))
    },
    copyDay(from, to) {
      if (!from || !to) return
      this.form.days[to] = JSON.parse(JSON.stringify(this.form.days[from]))
      this.copyFrom = ''
      this.copyToDay = ''
    },
  },
}
</script>

<style scoped>
.sidebar-content {
  padding: 2rem 1.5rem 1.5rem 1.5rem;
}
</style>
